import { c as _c } from "react/compiler-runtime";
import { add, areIntervalsOverlapping, endOfDay, getDate, isSameDay, parse, startOfDay, startOfHour, sub } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Datepicker } from '@datepicker-react/styled';
import { formatDate, relative_date_presets, relative_dates, resolveRelativeDate, setTimeInTimezone } from '../../../../utils/date';
import { useDatePreferences } from '../../../hooks/useDatePreferences';
import { StyledCard, StyledCardSection } from '../../base/Card';
import Icon from '../../base/Icon';
import Tooltip from '../../base/Tooltip';
import { Div } from '../../helpers/StyledUtils';
import TimezoneSelectInput, { BROWSER_TIMEZONE_OPTION, resolveTimezoneOption } from './TimezoneSelectInput';
import TimeInput, { time_input_steps_by_format } from './TimeInput';
import Button from '../../base/Button';
const SelectableButton = styled(Button)`
  justify-content: flex-start;
`;
const StyledTimeInputWrapper = styled(Div)`
  flex: 1 1 0;
  min-width: 0;
`;
const StyledTimezoneInputWrapper = styled(Div)`
  flex: 1.4 1 0;
  min-width: 0;
`;
const getRelativeDatePresetUnit = (key: (typeof relative_date_presets)[number]) => {
  if (key.includes('minute')) {
    return 'minute';
  }
  if (key.includes('hour')) {
    return 'hour';
  }
  return 'day';
};
const RelativeCellWrapper = styled.div<{
  active: boolean;
}>(({
  theme,
  active
}) => active && css`
      height: 32px;
      width: 32px;
      position: relative;
      left: 1px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${theme.colors.surface.container.primary};
      color: ${theme.colors.on.hue_container.primary};
      box-shadow: ${theme.mode === 'dark' ? theme.shadows.neutral.highlight : css`
          1px 0 0 0 #ccdaf5,
          0 1px 0 0 #ccdaf5,
          1px 1px 0 0 #ccdaf5,
          1px 0 0 0 #ccdaf5 inset,
          0 1px 0 0 #ccdaf5 inset;
        `};
      z-index: 1;
    `);

// this needs to happen in the onSubmit to avoid date range errors
// that way the validation will always run first and avoid errors
export const formatDateFilterValues = (values: {
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
}, timezone: string) => {
  values.start_date = values.start_date && values.start_time ? setTimeInTimezone(values.start_date, values.start_time, timezone).toISOString() : values.start_date;
  values.end_date = values.end_date && values.end_time ? setTimeInTimezone(values.end_date, values.end_time, timezone).toISOString() : values.end_date;
  return {
    start_date: values.start_date,
    end_date: values.end_date
  };
};
const DatePickerInput: React.FC<{
  retention_days: number;
  name?: string;
  display_reset?: boolean;
  time_format?: keyof typeof time_input_steps_by_format;
}> = t0 => {
  const $ = _c(90);
  const {
    retention_days,
    name: t1,
    time_format
  } = t0;
  const name = t1 === undefined ? "date" : t1;
  const {
    browser_timezone,
    first_day_of_week,
    timezone,
    uses_browser_timezone
  } = useDatePreferences();
  const [focused_input, setFocusedInput] = useState("startDate");
  const [t2,, t3] = useField(name);
  const {
    value
  } = t2;
  const {
    setValue
  } = t3;
  const [t4] = useField("timezone");
  const {
    value: timezone_option
  } = t4;
  const [t5, t6, t7] = useField("start_time");
  const {
    value: start_time_value
  } = t5;
  const {
    initialValue: start_time_initial_value
  } = t6;
  const {
    setValue: setStartTimeValue
  } = t7;
  const [t8, t9, t10] = useField("end_time");
  const {
    value: end_time_value
  } = t8;
  const {
    initialValue: end_time_initial_value
  } = t9;
  const {
    setValue: setEndTimeValue
  } = t10;
  let t11;
  if ($[0] !== end_time_initial_value || $[1] !== start_time_initial_value) {
    t11 = {
      start: start_time_initial_value,
      end: end_time_initial_value
    };
    $[0] = end_time_initial_value;
    $[1] = start_time_initial_value;
    $[2] = t11;
  } else {
    t11 = $[2];
  }
  const [time_reset_value, setTimeResetValue] = useState(t11);
  let t12;
  if ($[3] !== value?.min) {
    t12 = () => {
      if (!value?.min) {
        setFocusedInput("startDate");
      }
    };
    $[3] = value?.min;
    $[4] = t12;
  } else {
    t12 = $[4];
  }
  let t13;
  if ($[5] !== value) {
    t13 = [value];
    $[5] = value;
    $[6] = t13;
  } else {
    t13 = $[6];
  }
  useEffect(t12, t13);
  let t14;
  if ($[7] !== value) {
    t14 = value?.relative && resolveRelativeDate(value.relative);
    $[7] = value;
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  const relative_sample_date = t14;
  let T0;
  let T1;
  let T2;
  let T3;
  let handleTimezoneChange;
  let now;
  let picker_timezone;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  if ($[9] !== browser_timezone || $[10] !== first_day_of_week || $[11] !== retention_days || $[12] !== setEndTimeValue || $[13] !== setStartTimeValue || $[14] !== setValue || $[15] !== timezone || $[16] !== timezone_option || $[17] !== uses_browser_timezone || $[18] !== value) {
    picker_timezone = resolveTimezoneOption(timezone_option || (uses_browser_timezone ? BROWSER_TIMEZONE_OPTION : timezone), browser_timezone);
    now = new Date();
    const zoned_now = utcToZonedTime(now, picker_timezone);
    let t29;
    if ($[40] !== browser_timezone || $[41] !== setEndTimeValue || $[42] !== setStartTimeValue || $[43] !== value) {
      t29 = next_timezone_option => {
        const next_timezone = resolveTimezoneOption(next_timezone_option, browser_timezone);
        const next_start_time = value?.min ? formatDate(value.min, "HH:mm:ss", next_timezone) : undefined;
        const next_end_time = value?.max ? formatDate(value.max, "HH:mm:ss", next_timezone) : undefined;
        if (next_start_time) {
          setStartTimeValue(next_start_time);
        }
        if (next_end_time) {
          setEndTimeValue(next_end_time);
        }
        setTimeResetValue(previous => ({
          start: next_start_time ?? previous.start,
          end: next_end_time ?? previous.end
        }));
      };
      $[40] = browser_timezone;
      $[41] = setEndTimeValue;
      $[42] = setStartTimeValue;
      $[43] = value;
      $[44] = t29;
    } else {
      t29 = $[44];
    }
    handleTimezoneChange = t29;
    T3 = Div;
    t26 = 4;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = {
        gap: 6
      };
      $[45] = t27;
    } else {
      t27 = $[45];
    }
    let t30;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        direction: "column",
        gap: 1.5
      };
      $[46] = t30;
    } else {
      t30 = $[46];
    }
    t28 = <Div flex={t30}>{relative_date_presets.map((key, index) => {
        const previous_key = relative_date_presets[index - 1];
        const {
          label,
          days,
          convert
        } = relative_dates[key];
        const has_unit_spacing = previous_key && getRelativeDatePresetUnit(previous_key) !== getRelativeDatePresetUnit(key);
        return <Div key={key} m={{
          t: has_unit_spacing ? 3 : 0
        }}><Tooltip disabled={days <= retention_days} tooltip="Upgrade to increase retention" side="right" offset={[-16, 4]} fill={true} cta={{
            icon: "upgrade",
            label: "Upgrade",
            to: "/settings/organization/plans?highlight=retention_days"
          }}><SelectableButton ghost={true} block={true} small={true} active={value?.relative === key} disabled={days > retention_days} onClick={() => {
              const {
                min,
                max
              } = convert(now);
              setValue({
                relative: key,
                min: min.toISOString(),
                max: max.toISOString()
              });
              min && setStartTimeValue(formatDate(min, "HH:mm:ss", picker_timezone));
              max && setEndTimeValue(formatDate(max, "HH:mm:ss", picker_timezone));
            }}>{label}</SelectableButton></Tooltip></Div>;
      })}</Div>;
    T2 = StyledCard;
    T1 = StyledCardSection;
    t25 = 4;
    T0 = Datepicker;
    t15 = first_day_of_week;
    t16 = false;
    t17 = false;
    t18 = false;
    t19 = data => {
      setFocusedInput(data.focusedInput || "startDate");
      const min_0 = data.startDate ? zonedTimeToUtc(startOfDay(data.startDate), picker_timezone) : undefined;
      const max_0 = data.endDate ? isSameDay(data.endDate, zoned_now) ? zonedTimeToUtc(add(startOfHour(zoned_now), {
        hours: 1
      }), picker_timezone) : isSameDay(data.endDate, data.startDate) ? zonedTimeToUtc(endOfDay(data.endDate), picker_timezone) : zonedTimeToUtc(startOfDay(data.endDate), picker_timezone) : zonedTimeToUtc(endOfDay(data.startDate), picker_timezone);
      setValue({
        relative: undefined,
        min: min_0?.toISOString() || undefined,
        max: max_0?.toISOString() || undefined
      });
      min_0 && setStartTimeValue(formatDate(min_0, "HH:mm:ss", picker_timezone));
      max_0 && setEndTimeValue(formatDate(max_0, "HH:mm:ss", picker_timezone));
      setTimeResetValue(prev => ({
        start: min_0 ? formatDate(min_0, "HH:mm:ss", picker_timezone) : prev.start,
        end: max_0 ? formatDate(max_0, "HH:mm:ss", picker_timezone) : prev.end
      }));
    };
    t20 = sub(zoned_now, {
      days: retention_days
    });
    t21 = zoned_now;
    t22 = value?.min && utcToZonedTime(value.min, picker_timezone) || sub(zoned_now, {
      days: retention_days
    });
    t23 = !value?.relative && value?.min ? utcToZonedTime(value.min, picker_timezone) : null;
    t24 = !value?.relative && value?.max ? utcToZonedTime(value.max, picker_timezone) : null;
    $[9] = browser_timezone;
    $[10] = first_day_of_week;
    $[11] = retention_days;
    $[12] = setEndTimeValue;
    $[13] = setStartTimeValue;
    $[14] = setValue;
    $[15] = timezone;
    $[16] = timezone_option;
    $[17] = uses_browser_timezone;
    $[18] = value;
    $[19] = T0;
    $[20] = T1;
    $[21] = T2;
    $[22] = T3;
    $[23] = handleTimezoneChange;
    $[24] = now;
    $[25] = picker_timezone;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
    $[34] = t23;
    $[35] = t24;
    $[36] = t25;
    $[37] = t26;
    $[38] = t27;
    $[39] = t28;
  } else {
    T0 = $[19];
    T1 = $[20];
    T2 = $[21];
    T3 = $[22];
    handleTimezoneChange = $[23];
    now = $[24];
    picker_timezone = $[25];
    t15 = $[26];
    t16 = $[27];
    t17 = $[28];
    t18 = $[29];
    t19 = $[30];
    t20 = $[31];
    t21 = $[32];
    t22 = $[33];
    t23 = $[34];
    t24 = $[35];
    t25 = $[36];
    t26 = $[37];
    t27 = $[38];
    t28 = $[39];
  }
  let t29;
  if ($[47] !== now || $[48] !== picker_timezone || $[49] !== relative_sample_date) {
    t29 = day => <RelativeCellWrapper active={relative_sample_date && areIntervalsOverlapping({
      start: startOfDay(day),
      end: endOfDay(day)
    }, {
      start: utcToZonedTime(relative_sample_date.min, picker_timezone),
      end: utcToZonedTime(relative_sample_date.max || now, picker_timezone)
    })}>{getDate(day)}</RelativeCellWrapper>;
    $[47] = now;
    $[48] = picker_timezone;
    $[49] = relative_sample_date;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== T0 || $[52] !== focused_input || $[53] !== t15 || $[54] !== t16 || $[55] !== t17 || $[56] !== t18 || $[57] !== t19 || $[58] !== t20 || $[59] !== t21 || $[60] !== t22 || $[61] !== t23 || $[62] !== t24 || $[63] !== t29) {
    t30 = <T0 firstDayOfWeek={t15} showClose={t16} showSelectedDates={t17} showResetDates={t18} onDatesChange={t19} minBookingDate={t20} maxBookingDate={t21} initialVisibleMonth={t22} startDate={t23} endDate={t24} focusedInput={focused_input} onDayRender={t29} />;
    $[51] = T0;
    $[52] = focused_input;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== T1 || $[66] !== t25 || $[67] !== t30) {
    t31 = <T1 p={t25}>{t30}</T1>;
    $[65] = T1;
    $[66] = t25;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== end_time_value || $[70] !== handleTimezoneChange || $[71] !== picker_timezone || $[72] !== setEndTimeValue || $[73] !== setStartTimeValue || $[74] !== setValue || $[75] !== start_time_value || $[76] !== time_format || $[77] !== time_reset_value || $[78] !== value) {
    t32 = time_format && <StyledCardSection p={{
      x: 4,
      y: 3
    }} flex={{
      grow: true,
      gap: 3,
      align: "flex-start"
    }}><StyledTimeInputWrapper><TimeInput label_size="xs" format={time_format} reset_value={time_reset_value.start} onReset={() => {
          setStartTimeValue(time_reset_value.start);
        }} onChange={() => {
          setValue({
            ...value,
            relative: undefined
          });
        }} validate={time => {
          const now_0 = new Date();
          if (isSameDay(utcToZonedTime(value.min, picker_timezone), utcToZonedTime(value.max, picker_timezone)) && parse(time, "HH:mm:ss", now_0) > parse(end_time_value, "HH:mm:ss", now_0)) {
            return "Start time must be before end time";
          }
        }} label="Start time" name="start_time" /></StyledTimeInputWrapper><Div><Icon muted={true} m={{
          t: 8,
          x: -1
        }} icon="arrow_forward" /></Div><StyledTimeInputWrapper><TimeInput label_size="xs" format={time_format} reset_value={time_reset_value.end} onReset={() => {
          setEndTimeValue(time_reset_value.end);
        }} onChange={() => {
          setValue({
            ...value,
            relative: undefined
          });
        }} validate={time_0 => {
          const now_1 = new Date();
          if (isSameDay(utcToZonedTime(value.min, picker_timezone), utcToZonedTime(value.max, picker_timezone)) && parse(time_0, "HH:mm:ss", now_1) < parse(start_time_value, "HH:mm:ss", now_1)) {
            return "End time must be after start time";
          }
        }} label="End time" name="end_time" /></StyledTimeInputWrapper><StyledTimezoneInputWrapper><TimezoneSelectInput compact_browser_default={true} portal={true} name="timezone" m={0} onChange={handleTimezoneChange} dropdown={{
          dropdown_width: 360
        }} /></StyledTimezoneInputWrapper></StyledCardSection>;
    $[69] = end_time_value;
    $[70] = handleTimezoneChange;
    $[71] = picker_timezone;
    $[72] = setEndTimeValue;
    $[73] = setStartTimeValue;
    $[74] = setValue;
    $[75] = start_time_value;
    $[76] = time_format;
    $[77] = time_reset_value;
    $[78] = value;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== T2 || $[81] !== t31 || $[82] !== t32) {
    t33 = <T2>{t31}{t32}</T2>;
    $[80] = T2;
    $[81] = t31;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== T3 || $[85] !== t26 || $[86] !== t27 || $[87] !== t28 || $[88] !== t33) {
    t34 = <T3 p={t26} flex={t27}>{t28}{t33}</T3>;
    $[84] = T3;
    $[85] = t26;
    $[86] = t27;
    $[87] = t28;
    $[88] = t33;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  return t34;
};
export default DatePickerInput;